const hasWorker = registration => registration &&
    [registration.installing, registration.waiting, registration.active]
        .some(worker => worker && worker.state !== 'redundant')

// One monitor per page. Reacquire registrations rather than retaining the
// registration captured at startup: it may have been removed while we ran.
export function createServiceWorkerHealth({ serviceWorker, scriptURL, scope,
    online = () => true, visible = () => true, now = Date.now,
    schedule = setTimeout, cancel = clearTimeout,
    report = () => {}, onRegistration = () => {} }) {
    const interval = 5 * 60 * 1000
    // lastCheck: last successful check. retryAt: failure backoff, which
    // every trigger except a forced one respects.
    let pending = null, timer = null, stopped = false, lastCheck = -Infinity, retryAt = 0, failures = 0
    let current = null, detach = () => {}
    const log = (event, fields = {}) => {
        try { report(event, { ...fields, failures }) } catch (_) { /* Telemetry cannot break recovery. */ }
    }
    function bind(registration) {
        if (current === registration) return
        detach()
        current = registration
        detach = onRegistration(registration) || (() => {})
    }
    function arm() {
        cancel(timer)
        const nextCheck = failures ? retryAt : lastCheck + interval
        if (!stopped) timer = schedule(() => { void check('timer') }, Math.max(1000, nextCheck - now()))
    }
    async function inspect(reason) {
        let stage = 'lookup'
        try {
            let registration = await serviceWorker.getRegistration(scope)
            if (stopped) return
            // A root-scope lookup must not attach to a different app's worker.
            if (registration && registration.scope !== scope) registration = null
            let restored = false
            if (!hasWorker(registration)) {
                stage = 'register'
                registration = await serviceWorker.register(scriptURL, { scope })
                restored = true
            }
            if (stopped) return
            if (!hasWorker(registration)) throw new Error('Registration has no worker')
            bind(registration)
            // Registration already performs an update check. Avoid trying to
            // update again while a new installation is in progress.
            if (!restored && !registration.installing) {
                stage = 'update'
                try {
                    await registration.update()
                } catch (error) {
                    if (error?.name !== 'InvalidStateError') throw error
                    // The worker can vanish after the check above. One bounded
                    // re-registration handles this race without deleting caches.
                    stage = 'recover'
                    registration = await serviceWorker.register(scriptURL, { scope })
                    if (stopped) return
                    if (!hasWorker(registration)) throw new Error('Recovery returned no worker')
                    bind(registration)
                    restored = true
                }
            }
            if (stopped) return
            if (restored) log('registration_restored', { reason, stage })
            if (failures) log('checks_resumed', { reason })
            failures = 0
            lastCheck = now()
        } catch (error) {
            if (stopped) return
            failures++
            const retryMs = Math.min(30 * 60 * 1000, 30000 * 2 ** Math.min(failures - 1, 6))
            retryAt = now() + retryMs
            log('check_failed', { reason, stage, retry_ms: retryMs,
                error_name: error?.name || 'Error', error_message: String(error?.message || error).slice(0, 300) })
        }
    }
    // gap: how long after the last successful check this trigger may
    // check again. It defaults to the full interval, which paces the
    // background timer; callers pass a short gap for route changes and
    // foregrounding. Gating every trigger on the interval meant a deploy
    // notification or a route change within five minutes of the last
    // check was a no-op, so a new build sat unnoticed until the timer
    // came round. force (a deploy notification, coming back online) skips
    // both the gap and the failure backoff: each is a single event, not a
    // burst, and exactly the moment a check is most likely to find news.
    function check(reason = 'manual', { gap = interval, force = false } = {}) {
        if (stopped) return Promise.resolve()
        // A check already in flight may have fetched the worker script
        // before the deploy landed; a forced trigger checks again after it.
        if (pending) return force ? pending.then(() => check(reason, { force })) : pending
        if (!force && (now() < retryAt || now() < lastCheck + gap)) return Promise.resolve()
        if (!online() || !visible()) {
            // Poll lightly while suspended; foreground/online events can check
            // immediately because they do not advance the failure backoff.
            cancel(timer)
            timer = schedule(() => { void check('timer') }, interval)
            return Promise.resolve()
        }
        pending = inspect(reason).finally(() => {
            pending = null
            arm()
        })
        return pending
    }
    return {
        check,
        stop() { stopped = true; cancel(timer); detach(); current = null }
    }
}
